/**
 * Which open tasks this device has already shown. A task the device has never
 * had on screen floats to the top of the open board with a "New" pill until
 * its card has been in view; a task the peon added themselves counts as seen
 * from the start. Per device only (localStorage, `op.seenTasks`); the server
 * knows nothing about it. See docs/new-tasks.md.
 *
 * The pure parts (unseenIds, pinNew, pruneSeen) are tested in
 * tests/seen.test.ts; only readSeen/writeSeen touch storage.
 */
const KEY = "op.seenTasks";

/** Null when this device has no record yet, so a first launch baselines instead of flagging every task new. */
export function readSeen(): Set<string> | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw === null) return null;
    const list = JSON.parse(raw) as unknown;
    return new Set(Array.isArray(list) ? list.filter((x): x is string => typeof x === "string") : []);
  } catch {
    return null;
  }
}

export function writeSeen(ids: Iterable<string>): void {
  try {
    localStorage.setItem(KEY, JSON.stringify([...ids]));
  } catch {
    /* storage disabled: every launch baselines, nothing is ever pinned */
  }
}

/**
 * Ids of `tasks` this device has not shown yet. Tasks the peon added
 * themselves are never new to them. A null record (no history) yields none.
 */
export function unseenIds(tasks: ReadonlyArray<{ id: string; created_by: string }>, seen: ReadonlySet<string> | null, peonName: string): string[] {
  if (seen === null) return [];
  return tasks.filter((t) => !seen.has(t.id) && t.created_by !== peonName).map((t) => t.id);
}

/** `pinned` tasks first, then the rest; the relative order within each group is untouched. */
export function pinNew<T extends { id: string }>(tasks: ReadonlyArray<T>, pinned: ReadonlySet<string>): T[] {
  if (pinned.size === 0) return [...tasks];
  return [...tasks.filter((t) => pinned.has(t.id)), ...tasks.filter((t) => !pinned.has(t.id))];
}

// The open board's order on this device (the server's: urgent, then the chores, then luxury, then
// the rest by waiting time; urgent and luxury tasks are never under NEW, then NEW, then the rest)
// is shared with the Expo client: shared/nextUp.ts liftNew, re-exported here.
export { liftNew } from "../shared/nextUp.ts";

/**
 * The record to keep after a load: what was seen and is still open, plus the
 * peon's own tasks. Dropping ids that left the open list keeps the record
 * small; a task reopened later reads as new again, which is fair.
 */
export function pruneSeen(seen: ReadonlySet<string>, tasks: ReadonlyArray<{ id: string; created_by: string }>, peonName: string): Set<string> {
  const next = new Set<string>();
  for (const t of tasks) if (seen.has(t.id) || t.created_by === peonName) next.add(t.id);
  return next;
}
